import { addEtDays, getETDate } from '@/lib/marketTime';

/** How far ahead a report has to be before the badge stops caring. Two weeks:
 *  far enough that a position can still be sized or trimmed around the event,
 *  which one week was not. The tiers below keep the extra week from shouting. */
export const EARNINGS_BADGE_LOOKAHEAD_DAYS = 14;

export function getEarningsBadgeWindow(now = new Date()): { from: string; to: string } {
  const from = getETDate(now);
  return { from, to: addEtDays(from, EARNINGS_BADGE_LOOKAHEAD_DAYS) };
}

export function getUpcomingEarningsSymbols(
  entries: ReadonlyArray<{ ticker: string; date: string }>,
  from: string,
  to: string,
): string[] {
  return Array.from(getUpcomingEarningsDates(entries, from, to).keys());
}

/** Earliest upcoming earnings date per ticker inside the badge window. */
export function getUpcomingEarningsDates(
  entries: ReadonlyArray<{ ticker: string; date: string }>,
  from: string,
  to: string,
): Map<string, string> {
  const dates = new Map<string, string>();
  for (const entry of entries) {
    if (entry.date < from || entry.date > to) continue;
    const ticker = entry.ticker.toUpperCase();
    const existing = dates.get(ticker);
    if (!existing || entry.date < existing) dates.set(ticker, entry.date);
  }
  return dates;
}

/** Whole ET days from `today` until an earnings date, both 'YYYY-MM-DD'.
 *  Parsed as UTC midnight on both sides so the browser timezone cannot shift
 *  either by a day. Negative once the date has passed; null if unparseable. */
export function earningsDaysUntil(date: string, today = getETDate()): number | null {
  const target = Date.parse(`${date}T00:00:00Z`);
  const base = Date.parse(`${today}T00:00:00Z`);
  if (!Number.isFinite(target) || !Number.isFinite(base)) return null;
  return Math.round((target - base) / 86_400_000);
}

/** Wording for a header earnings badge — "today" / "tomorrow" / "in 6d" —
 *  or null when the date falls outside the shared badge window. Same window
 *  the watchlist badges use, so the two never disagree about whether a
 *  report is imminent. */
export function earningsBadgeWhen(date: string, today = getETDate()): string | null {
  const days = earningsDaysUntil(date, today);
  if (days == null || days < 0 || days > EARNINGS_BADGE_LOOKAHEAD_DAYS) return null;
  return days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days}d`;
}

/**
 * Urgency tier for an earnings badge, or null when the report is outside the
 * shared window.
 *
 * Three tiers rather than one flat colour, because a 14-day window puts a
 * report that is two weeks away and one that is tomorrow in the same list, and
 * they are not the same information. Callers map these to colours through
 * EARNINGS_TIER_CLASSES so every badge in the app agrees.
 */
export type EarningsBadgeTier = 'today' | 'tomorrow' | 'upcoming';

export function earningsBadgeTier(date: string, today = getETDate()): EarningsBadgeTier | null {
  const days = earningsDaysUntil(date, today);
  if (days == null || days < 0 || days > EARNINGS_BADGE_LOOKAHEAD_DAYS) return null;
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return 'upcoming';
}

/**
 * Badge palette per tier, escalating as the event arrives so the row does not
 * look identical for a fortnight.
 *
 * Teal for "on the radar" — calm, and clear of the colours already spoken for
 * in a watchlist row (sky ticker, emerald/red price pill, amber alert bell).
 * Violet the day before: the badge's long-standing colour, kept for the tier
 * that matters most day to day, so the familiar purple calendar still means
 * "this reports tomorrow". Rose on the day itself.
 */
export const EARNINGS_TIER_CLASSES: Record<EarningsBadgeTier, string> = {
  upcoming: 'bg-teal-500/15 text-teal-300 border-teal-500/30',
  tomorrow: 'bg-violet-500/20 text-violet-300 border-violet-500/45',
  today:    'bg-rose-500/25 text-rose-300 border-rose-500/50',
};

/** Format a date-only earnings value without allowing the browser timezone
 *  to shift it to the prior day. */
export function formatEarningsBadgeTitle(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return `Earnings report: ${date}`;
  const value = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  const formatted = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(value);
  return `Earnings report: ${formatted}`;
}

/** Tooltip text for a glyph-only badge: the date plus how far out it is, so
 *  the colour tier is never the only way to read the urgency. */
export function formatEarningsBadgeTooltip(date: string, today = getETDate()): string {
  const when = earningsBadgeWhen(date, today);
  const base = formatEarningsBadgeTitle(date);
  return when ? `${base} (${when})` : base;
}
